import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { MdMoreVert } from 'react-icons/md';

/**
 * Reusable 3-dot action menu. Dropdown is portaled to body so it appears above other cards.
 * items: array of { label, icon, onClick, danger } | 'divider'
 */
const ActionMenu = ({ items, align = 'right' }) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, right: 0, left: 0 });
  const buttonRef = useRef();
  const dropdownRef = useRef();

  const updatePosition = (dropdownHeight = 0) => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      let top = rect.bottom + 4;
      if (dropdownHeight) {
        const maxTop = window.innerHeight - dropdownHeight - 8;
        const minTop = 8;
        top = Math.min(Math.max(minTop, top), Math.max(minTop, maxTop));
      }
      setPosition({
        top,
        right: window.innerWidth - rect.right,
        left: rect.left,
      });
    }
  };

  useEffect(() => {
    if (open && buttonRef.current) {
      // Initial position estimate
      updatePosition();
      // After dropdown renders, recalc using its actual height to avoid
      // being cut off at the bottom of the viewport
      requestAnimationFrame(() => {
        if (dropdownRef.current) {
          const h = dropdownRef.current.getBoundingClientRect().height;
          updatePosition(h);
        }
      });
      const onScroll = () => updatePosition();
      window.addEventListener('scroll', onScroll, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', onScroll, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [open, align]);

  useEffect(() => {
    const close = (e) => {
      if (
        buttonRef.current && !buttonRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const dropdownContent = open ? (
    <div
      ref={dropdownRef}
      className="action-dropdown action-dropdown-portaled"
      style={{
        position: 'fixed',
        top: position.top,
        left: align === 'right' ? undefined : position.left,
        right: align === 'right' ? position.right : undefined,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {items.map((item, i) =>
        item === 'divider'
          ? <div key={i} className="action-dropdown-divider" />
          : (
            <button
              key={i}
              type="button"
              className={`action-dropdown-item${item.danger ? ' danger' : ''}`}
              onClick={() => { item.onClick(); setOpen(false); }}
            >
              {item.icon && (
                <span style={{ display: 'flex', color: item.danger ? 'var(--danger)' : 'var(--primary)' }}>
                  {item.icon}
                </span>
              )}
              {item.label}
            </button>
          )
      )}
    </div>
  ) : null;

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        ref={buttonRef}
        type="button"
        className="btn-icon"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        style={{ padding: 6 }}
        title="Actions"
      >
        <MdMoreVert size={18} />
      </button>
      {createPortal(dropdownContent, document.body)}
    </div>
  );
};

export default ActionMenu;
